import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeftRight } from "lucide-react";
import { TIPOS_MODALIDAD } from "@/lib/constants";
import { nombreTourDisplay } from "@/lib/tourDisplay";

function opcionLabel(m) {
  const modalidad = TIPOS_MODALIDAD[m.modalidad] || m.modalidad;
  const veh = m.vehiculo === "van" ? "Van" : "Bus";
  const idioma = m.idioma === "combinada" ? "Bilingüe" : m.idioma === "en" ? "EN" : "ES";
  const prefijo = m.tour_nombre ? `${nombreTourDisplay(m.tour_nombre, m.modalidad)} · ` : "";
  return `${prefijo}${modalidad} · ${veh} · ${idioma} (${m.cupo_usado}/${m.cupo_max})`;
}

export default function MoverReservaGrupoDialog({ open, onOpenChange, reserva, miembros = [], onMoved }) {
  const [salidaId, setSalidaId] = useState("");
  const [saving, setSaving] = useState(false);

  const opciones = miembros.filter((m) => m.id !== reserva?.salida_id);

  useEffect(() => {
    if (open) setSalidaId("");
  }, [open]);

  const mover = async () => {
    if (!salidaId) {
      toast.error("Elegí la salida destino.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/reservas/${reserva.id}/mover-grupo`, { salida_id: salidaId });
      toast.success("Reserva movida a la otra salida del grupo.");
      onOpenChange(false);
      onMoved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="mover-grupo-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-indigo-600" /> Mover a otra salida del grupo
          </DialogTitle>
          <DialogDescription>
            {reserva?.pasajero?.nombre} {reserva?.pasajero?.apellido} pasa a otra salida combinada en
            el MISMO cupo real -- no cambia el cupo total del grupo, solo redistribuye el desglose
            (puede ser de otro tour, ej. de la van VIP al bus regular).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label className="text-xs text-slate-500">Salida destino</Label>
          <Select value={salidaId} onValueChange={setSalidaId}>
            <SelectTrigger data-testid="mover-grupo-select">
              <SelectValue placeholder="Elegí una salida..." />
            </SelectTrigger>
            <SelectContent>
              {opciones.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {opcionLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={mover} disabled={saving || !salidaId} data-testid="mover-grupo-confirmar-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mover reserva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
